import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, FormHelp, Label, LabeledControl, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError, useRentalManagerUserPropertyEditWebApiHandler_pets } from '@zg-rentals/rental-manager-env';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import useForm from '../../../hooks/useForm';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import { postingConstraints } from '../../../utils/formValidation';
import propertyUtils from '../../../utils/propertyUtils';
import { getIsInputFormDisabled } from '../../MFAccordion/utils';

import { useAuth } from '../../AuthProvider';

import type { GetUserPropertyDetailsPetAllowedAndCountWebDto } from '@zg-rentals/ts-rental-manager-api-client';

type PetKey = 'none' | 'catsDto' | 'smallDogsDto' | 'largeDogsDto';

const normalizePetAllowedAndCountDto = (
  dto?: Partial<GetUserPropertyDetailsPetAllowedAndCountWebDto> | null,
): GetUserPropertyDetailsPetAllowedAndCountWebDto => {
  const allowed = dto?.allowed === true;
  return {
    allowed,
    count: allowed ? (dto?.count ?? 1) : 0,
  };
};

const PetPolicyFormField = ({ isRelistingFieldset = false }: { isRelistingFieldset?: boolean }) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { mutateAsync: updatePets } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_pets,
    successToastMessage: 'Pet policy updated',
    meta: { context: { api: 'petsAllowed', details: { listingAlias } } },
  });

  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const rawPetsAllowed = listingDetails?.petsAllowed;
  const petsAllowed = {
    ...rawPetsAllowed,
    catsDto: normalizePetAllowedAndCountDto(rawPetsAllowed?.catsDto),
    smallDogsDto: normalizePetAllowedAndCountDto(rawPetsAllowed?.smallDogsDto),
    largeDogsDto: normalizePetAllowedAndCountDto(rawPetsAllowed?.largeDogsDto),
    none: propertyUtils.getIsPetsAllowedEmpty(rawPetsAllowed),
  };
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [errorMessage, setErrorMessage] = useState('');

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      petsAllowed,
    },
    postingConstraints,
  );

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const getUpdatedPetsAllowed = (key: PetKey) => {
    const newPetsAllowed = { ...inputs?.petsAllowed };

    // Clicking any pet type should deselect "none"
    if (key !== 'none') {
      newPetsAllowed.none = false;
    }

    switch (key) {
      case 'none':
        newPetsAllowed.catsDto = normalizePetAllowedAndCountDto({ allowed: false });
        newPetsAllowed.smallDogsDto = normalizePetAllowedAndCountDto({ allowed: false });
        newPetsAllowed.largeDogsDto = normalizePetAllowedAndCountDto({ allowed: false });
        newPetsAllowed.none = true;
        break;

      case 'catsDto':
      case 'smallDogsDto':
      case 'largeDogsDto': {
        const currentDto = inputs?.petsAllowed?.[key];
        newPetsAllowed[key] = normalizePetAllowedAndCountDto({
          allowed: !(currentDto?.allowed === true),
        });
        break;
      }

      default:
        break;
    }

    // If no pets are selected, automatically select "none"
    const hasAnyPet =
      newPetsAllowed.catsDto?.allowed || newPetsAllowed.smallDogsDto?.allowed || newPetsAllowed.largeDogsDto?.allowed;
    if (!hasAnyPet) {
      newPetsAllowed.none = true;
    }

    return newPetsAllowed;
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const newPetsAllowed = getUpdatedPetsAllowed(name as PetKey);

    // field error check needs to be above must click on cta check
    if (hasFieldError(name, value)) {
      setErrorMessage('Enter your pet policy');
      return;
    }

    const mockEvent = {
      target: {
        name: 'petsAllowed',
        value: newPetsAllowed,
      },
    };
    handleFormChange(mockEvent);

    try {
      await updatePets({
        postData: {
          listingAlias,
          cats: newPetsAllowed.catsDto,
          smallDogs: newPetsAllowed.smallDogsDto,
          largeDogs: newPetsAllowed.largeDogsDto,
        },
      });
    } catch (err) {
      const errorMessage = DatarouterError.getDisplayMessage(err, 'Pet policy couldn’t be saved. Try again.');
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  const fieldSetLabel = isRelistingFieldset ? 'What’s your pet policy?' : 'Pets';

  return (
    <div data-testid="PetPolicyFormField">
      <FieldSet
        disabled={isInputFormDisabled}
        legend={
          <Label required={!isMultifamily && !isInputFormDisabled} size={isRelistingFieldset ? 'lg' : 'md'}>
            {isMultifamily ? 'Allowed' : fieldSetLabel}
          </Label>
        }
        error={!!errorMessage}
        description={<FormHelp>{errorMessage}</FormHelp>}
      >
        <LabeledControl
          label={<Label>No pets allowed</Label>}
          control={<Checkbox name="none" checked={inputs?.petsAllowed?.none === true} onChange={handleChange} />}
        />
        <LabeledControl
          label={<Label>Cats allowed</Label>}
          control={
            <Checkbox name="catsDto" checked={inputs?.petsAllowed?.catsDto?.allowed === true} onChange={handleChange} />
          }
        />
        <LabeledControl
          label={<Label>Small dogs allowed</Label>}
          control={
            <Checkbox
              name="smallDogsDto"
              checked={inputs?.petsAllowed?.smallDogsDto?.allowed === true}
              onChange={handleChange}
            />
          }
        />
        <LabeledControl
          label={<Label>Large dogs allowed</Label>}
          control={
            <Checkbox
              name="largeDogsDto"
              checked={inputs?.petsAllowed?.largeDogsDto?.allowed === true}
              onChange={handleChange}
            />
          }
        />
      </FieldSet>
    </div>
  );
};

export default memo(PetPolicyFormField);
